import { useState } from 'react';
import { Table, Typography, Tag, Switch, Space } from 'antd';
import { CodeOutlined, ReadOutlined } from '@ant-design/icons';
import { ProductAttribute } from '../../types/product';

const { Text } = Typography;

// Attribute names that contain HTML content
const HTML_ATTRIBUTE_NAMES = [
  'marketing information',
  'long description',
  'marketing text',
  'product description',
];

// Check if a string contains HTML tags
const containsHtml = (str: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(str);
};

// Component to render HTML content with toggle
function HtmlValueCell({ value }: { value: string }) {
  const [showSource, setShowSource] = useState(false);

  return (
    <div>
      <Space size="small" style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {showSource ? <CodeOutlined /> : <ReadOutlined />}
        </Text>
        <Switch
          size="small"
          checked={showSource}
          onChange={setShowSource}
          checkedChildren="HTML"
          unCheckedChildren="View"
        />
      </Space>
      {showSource ? (
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 11,
            fontFamily: 'monospace',
            background: 'var(--ant-color-fill-tertiary)',
            padding: 8,
            borderRadius: 4,
            maxHeight: 300,
            overflow: 'auto',
            margin: 0,
          }}
        >
          {value}
        </pre>
      ) : (
        <div
          style={{
            maxHeight: 300,
            overflow: 'auto',
            lineHeight: 1.5,
            fontSize: 13,
          }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      )}
    </div>
  );
}

interface AttributesTableProps {
  attributes: ProductAttribute[];
}

export function AttributesTable({ attributes }: AttributesTableProps) {
  if (!attributes || attributes.length === 0) {
    return <Text type="secondary">No attributes available</Text>;
  }

  // Handle different field naming conventions from various providers
  // External provider uses: name, displayvalue, unit, groupname
  // Etilize provider uses: attribute_name, displayvalue, unit_name, feature_group
  const dataSource = attributes.map((attr, index) => {
    const a = attr as unknown as Record<string, unknown>;
    const name = (a.name as string) || (a.attribute_name as string) || '';
    const value = (a.displayvalue as string) || (a.value as string) || '';
    return {
      key: index,
      name,
      value,
      isHtml: HTML_ATTRIBUTE_NAMES.includes(name.toLowerCase()) || containsHtml(value),
      unit: (a.unit as string) || (a.unit_name as string) || null,
      group: (a.groupname as string) || (a.feature_group as string) || null,
    };
  });

  const columns = [
    {
      title: 'Attribute',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '40%',
      render: (value: string, record: { isHtml: boolean }) =>
        record.isHtml ? <HtmlValueCell value={value} /> : value,
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: '15%',
      render: (unit: string | null) =>
        unit ? <Tag color="blue">{unit}</Tag> : '-',
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      width: '15%',
      render: (group: string | null) =>
        group ? <Tag>{group}</Tag> : '-',
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={{ pageSize: 20 }}
      size="small"
    />
  );
}
